/**
 * Generated by orval v6.31.0 🍺
 * Do not edit manually.
 * Report Server API
 * Report Server API - 신고 관리 시스템

## 주요 기능
- 신고 생성 및 조회
- 신고 검토 및 처리 (관리자)
- 신고 통계 분석
- 우선순위 관리
- 증거 자료 관리
- 다양한 타입 지원 (코스, 사용자, 댓글, 게시글 등)

## 아키텍처
- Spring Data JPA (PostgreSQL)
- OAuth2 JWT 기반 인증
- 관리자 권한 기반 검토 시스템
- AI 모더레이션 점수 지원

## 신고 처리 플로우
1. PENDING (접수됨) → 신고 생성
2. REVIEWING (검토 중) → 관리자 검토 시작
3. RESOLVED (처리 완료) 또는 REJECTED (기각됨) → 최종 결정
4. APPEALED (항소됨) → 재검토 요청
5. CLOSED (종료됨) → 완전 종료

 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  ApproveReportRequest,
  ChangePriorityRequest,
  DeleteReportResponse,
  ErrorResponse,
  GetAllReportsParams,
  RejectReportRequest,
  Report,
  StartReviewRequest,
  UpdateReportResponse,
} from "./reportServerAPI.schemas";
import { customInstance } from "../custom-axios";
import type { ErrorType } from "../custom-axios";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * 관리자가 모든 신고 목록을 조회합니다. 다양한 필터링 옵션을 제공합니다.
- 상태별 필터링 (PENDING, REVIEWING, RESOLVED, REJECTED)
- 우선순위별 필터링
- 대상 타입별 필터링
- 신고 사유별 필터링
- 날짜 범위 필터링

 * @summary 모든 신고 조회 (관리자)
 */
export const getAllReports = (
  params?: GetAllReportsParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<Report[]>(
    { url: `/api/reports`, method: "GET", params, signal },
    options,
  );
};

export const getGetAllReportsQueryKey = (params?: GetAllReportsParams) => {
  return [`/api/reports`, ...(params ? [params] : [])] as const;
};

export const getGetAllReportsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllReports>>,
  TError = ErrorType<ErrorResponse>,
>(
  params?: GetAllReportsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllReports>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllReportsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllReports>>> = ({
    signal,
  }) => getAllReports(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllReports>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAllReportsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAllReports>>
>;
export type GetAllReportsQueryError = ErrorType<ErrorResponse>;

/**
 * @summary 모든 신고 조회 (관리자)
 */
export const useGetAllReports = <
  TData = Awaited<ReturnType<typeof getAllReports>>,
  TError = ErrorType<ErrorResponse>,
>(
  params?: GetAllReportsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAllReports>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetAllReportsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * 신고를 삭제합니다. 관리자만 실행 가능합니다.
 * @summary 신고 삭제 (관리자)
 */
export const deleteReport = (
  id: string,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<DeleteReportResponse>(
    { url: `/api/reports/${id}`, method: "DELETE" },
    options,
  );
};

export const getDeleteReportMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteReport>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteReport>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteReport>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteReport(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteReportMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteReport>>
>;

export type DeleteReportMutationError = ErrorType<ErrorResponse>;

/**
 * @summary 신고 삭제 (관리자)
 */
export const useDeleteReport = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteReport>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteReport>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getDeleteReportMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * 신고 검토를 시작합니다. 상태가 REVIEWING으로 변경되고 검토자 정보가 기록됩니다.

 * @summary 신고 검토 시작 (관리자)
 */
export const startReview = (
  id: string,
  startReviewRequest: StartReviewRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<UpdateReportResponse>(
    {
      url: `/api/reports/${id}/review`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: startReviewRequest,
    },
    options,
  );
};

export const getStartReviewMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof startReview>>,
    TError,
    { id: string; data: StartReviewRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof startReview>>,
  TError,
  { id: string; data: StartReviewRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof startReview>>,
    { id: string; data: StartReviewRequest }
  > = (props) => {
    const { id, data } = props ?? {};

    return startReview(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StartReviewMutationResult = NonNullable<
  Awaited<ReturnType<typeof startReview>>
>;
export type StartReviewMutationBody = StartReviewRequest;
export type StartReviewMutationError = ErrorType<ErrorResponse>;

/**
 * @summary 신고 검토 시작 (관리자)
 */
export const useStartReview = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof startReview>>,
    TError,
    { id: string; data: StartReviewRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof startReview>>,
  TError,
  { id: string; data: StartReviewRequest },
  TContext
> => {
  const mutationOptions = getStartReviewMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * 신고를 승인하고 조치를 취합니다. 상태가 RESOLVED로 변경됩니다.
- 경고 발송
- 콘텐츠 삭제/숨김
- 사용자 정지/차단
- 연령 제한 설정
- 수익 창출 중지

 * @summary 신고 승인 (관리자)
 */
export const approveReport = (
  id: string,
  approveReportRequest: ApproveReportRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<UpdateReportResponse>(
    {
      url: `/api/reports/${id}/approve`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: approveReportRequest,
    },
    options,
  );
};

export const getApproveReportMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof approveReport>>,
    TError,
    { id: string; data: ApproveReportRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof approveReport>>,
  TError,
  { id: string; data: ApproveReportRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof approveReport>>,
    { id: string; data: ApproveReportRequest }
  > = (props) => {
    const { id, data } = props ?? {};

    return approveReport(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ApproveReportMutationResult = NonNullable<
  Awaited<ReturnType<typeof approveReport>>
>;
export type ApproveReportMutationBody = ApproveReportRequest;
export type ApproveReportMutationError = ErrorType<ErrorResponse>;

/**
 * @summary 신고 승인 (관리자)
 */
export const useApproveReport = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof approveReport>>,
    TError,
    { id: string; data: ApproveReportRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof approveReport>>,
  TError,
  { id: string; data: ApproveReportRequest },
  TContext
> => {
  const mutationOptions = getApproveReportMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * 신고를 기각합니다. 상태가 REJECTED로 변경됩니다.
부적절한 신고이거나 증거가 불충분한 경우 사용합니다.

 * @summary 신고 기각 (관리자)
 */
export const rejectReport = (
  id: string,
  rejectReportRequest: RejectReportRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<UpdateReportResponse>(
    {
      url: `/api/reports/${id}/reject`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: rejectReportRequest,
    },
    options,
  );
};

export const getRejectReportMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof rejectReport>>,
    TError,
    { id: string; data: RejectReportRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof rejectReport>>,
  TError,
  { id: string; data: RejectReportRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof rejectReport>>,
    { id: string; data: RejectReportRequest }
  > = (props) => {
    const { id, data } = props ?? {};

    return rejectReport(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RejectReportMutationResult = NonNullable<
  Awaited<ReturnType<typeof rejectReport>>
>;
export type RejectReportMutationBody = RejectReportRequest;
export type RejectReportMutationError = ErrorType<ErrorResponse>;

/**
 * @summary 신고 기각 (관리자)
 */
export const useRejectReport = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof rejectReport>>,
    TError,
    { id: string; data: RejectReportRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof rejectReport>>,
  TError,
  { id: string; data: RejectReportRequest },
  TContext
> => {
  const mutationOptions = getRejectReportMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * 신고의 우선순위를 변경합니다.
- LOW: 낮음
- NORMAL: 보통
- HIGH: 높음
- URGENT: 긴급
- CRITICAL: 심각

 * @summary 우선순위 변경 (관리자)
 */
export const changePriority = (
  id: string,
  changePriorityRequest: ChangePriorityRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<UpdateReportResponse>(
    {
      url: `/api/reports/${id}/priority`,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: changePriorityRequest,
    },
    options,
  );
};

export const getChangePriorityMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changePriority>>,
    TError,
    { id: string; data: ChangePriorityRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof changePriority>>,
  TError,
  { id: string; data: ChangePriorityRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof changePriority>>,
    { id: string; data: ChangePriorityRequest }
  > = (props) => {
    const { id, data } = props ?? {};

    return changePriority(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ChangePriorityMutationResult = NonNullable<
  Awaited<ReturnType<typeof changePriority>>
>;
export type ChangePriorityMutationBody = ChangePriorityRequest;
export type ChangePriorityMutationError = ErrorType<ErrorResponse>;

/**
 * @summary 우선순위 변경 (관리자)
 */
export const useChangePriority = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changePriority>>,
    TError,
    { id: string; data: ChangePriorityRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof changePriority>>,
  TError,
  { id: string; data: ChangePriorityRequest },
  TContext
> => {
  const mutationOptions = getChangePriorityMutationOptions(options);

  return useMutation(mutationOptions);
};
